import React, { useRef, useState, useEffect } from 'react'
import { APIWeb } from '../../api'
import { useTranslation } from 'react-i18next'

export interface UploadFileProps {
  /** 是否禁用 */
  disabled?: boolean
  /** 文件选择后的回调 */
  onChange?: (file: File | null) => void
  /** 当前文件 */
  value?: File | null
  /** 自定义样式类名 */
  className?: string
  /** 自定义样式 */
  style?: React.CSSProperties
  /** 子元素 */
  children?: React.ReactNode
  /** 最大文件大小（MB），默认5MB */
  maxSize?: number
  /** 文件大小超出限制时的回调 */
  onSizeExceed?: (file: File, maxSize: number) => void
  /** 文件类型不符合时的回调 */
  onTypeError?: (file: File) => void
  /** 是否自动上传，默认为 false */
  autoUpload?: boolean
  /** 上传成功回调，返回文件 URL */
  onUploadSuccess?: (fileUrl: string, file: File) => void
  /** 上传失败回调 */
  onUploadError?: (error: string, file: File) => void
  /** 上传进度变化回调 */
  onUploadProgress?: (progress: number, file: File) => void
}

const UploadFile: React.FC<UploadFileProps> = ({
    disabled = false,
    onChange,
    value,
    className = '',
    style,
    children,
    maxSize = 5, // 默认5MB
    onSizeExceed,
    onTypeError,
    autoUpload = false,
    onUploadSuccess,
    onUploadError,
    onUploadProgress,
}) => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    // 支持的图片类型
    const acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    const accept = acceptedImageTypes.join(',')

    // 真实的 S3 上传函数
    const uploadToS3 = async (file: File): Promise<string> => {
        try {
            setUploading(true)
            setUploadProgress(0)
            
            // 通知开始上传
            onUploadProgress?.(0, file)
            
            // 模拟线性进度增长
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    const newProgress = Math.min(prev + 10, 90) // 最多到90%，等待请求完成
                    onUploadProgress?.(newProgress, file)
                    return newProgress
                })
            }, 50) // 每50ms增加2%
            
            try {
                const s3Response = await APIWeb.s3uploadFile({
                    content_type: file.type
                })
                
                // 清除进度定时器
                clearInterval(progressInterval)
                
                if (!s3Response?.file_url) {
                    throw new Error('上传失败，未获取到文件地址')
                }
                
                // 上传成功，设置为100%
                setUploadProgress(100)
                onUploadProgress?.(100, file)
                
                return s3Response.file_url
                
            } catch (apiError) {
                // 清除进度定时器
                clearInterval(progressInterval)
                throw apiError
            }
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '上传失败'
            throw new Error(errorMessage)
        } finally {
            setUploading(false)
        }
    }

    // 将MB转换为字节
    const maxSizeInBytes = maxSize * 1024 * 1024

    // 验证文件是否为图片
    const isValidImageType = (file: File): boolean => {
        return acceptedImageTypes.includes(file.type)
    }

    // 处理文件选择和上传
    const handleFileSelect = async (file: File) => {
        // 验证文件类型
        if (!isValidImageType(file)) {
            onTypeError?.(file)
            return
        }

        // 验证文件大小
        if (file.size > maxSizeInBytes) {
            onSizeExceed?.(file, maxSize)
            return
        }

        // 更新文件状态
        onChange?.(file)

        // 如果启用自动上传，则开始上传
        if (autoUpload) {
            try {
                const fileUrl = await uploadToS3(file)
                
                // 上传成功回调
                onUploadSuccess?.(fileUrl, file)
                
            } catch (error) {
                // 上传失败时重置进度条
                setUploadProgress(0)
                
                const errorMessage = error instanceof Error ? error.message : '上传失败'
                onUploadError?.(errorMessage, file)
                
                // 上传失败时清除文件
                onChange?.(null)
            }
        }
    }

    // 暴露手动上传函数
    // const manualUpload = async (file: File): Promise<string> => {
    //     if (uploadFile) {
    //         return uploadFile(file)
    //     }
    //     return uploadToS3(file)
    // }

    // 将手动上传函数暴露给父组件
    // useImperativeHandle(uploadFile, () => ({
    //     upload: manualUpload
    // }), [uploadFile])

    // 生成图片预览URL
    useEffect(() => {
        if (value && isValidImageType(value)) {
            const url = URL.createObjectURL(value)
            setPreviewUrl(url)
            return () => URL.revokeObjectURL(url)
        } else {
            setPreviewUrl(null)
        }
    }, [value])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            handleFileSelect(file)
        }
        // 清空input值，允许重复选择同一文件
        event.target.value = ''
    }

    const handleClick = () => {
        if (!disabled && !uploading) {
            fileInputRef.current?.click()
        }
    }

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault()
        if (!disabled && !uploading) {
            setIsDragging(true)
        }
    }

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)

        if (disabled || uploading) return

        const files = event.dataTransfer.files
        if (files.length > 0) {
            const file = files[0]
            handleFileSelect(file)
        }
    }

    const handleRemove = (event: React.MouseEvent) => {
        event.stopPropagation()
        onChange?.(null)
    }

    // 如果有图片预览，显示预览
    if (previewUrl && value) {
        return (
            <div
                className={`
          relative cursor-pointer border-2 border-dashed rounded-lg transition-colors overflow-hidden
          ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
          ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
          ${className}
        `}
                style={style}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled || uploading}
                />

                {/* 图片预览 */}
                <div className="relative w-full h-full">
                    <img
                        src={previewUrl}
                        alt={t('预览图片')}
                        className="w-full h-full object-cover"
                    />

                    {/* 删除按钮 - 右上角 */}
                    <button
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleRemove(e)
                        }}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10 opacity-80 hover:opacity-100"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                
                    {/* 上传进度条 */}
                    {uploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                            <div className="w-3/4 bg-gray-300 rounded-full h-2 mb-2">
                                <div 
                                    className="bg-[var(--primary-color)] h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.round(uploadProgress)}%` }}
                                ></div>
                            </div>
                            <div className="text-white text-sm">
                                {t('上传中X', { progress: Math.round(uploadProgress) })}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        )
    }

    // 默认上传区域
    const defaultContent = (
        <div className="flex flex-col items-center justify-center p-6 text-center">
            {maxSize && (
                <div className="text-xs text-gray-400 mt-1">
                    {t('最大文件大小XMB', { size: maxSize })}MB
                </div>
            )}
        </div>
    )

    return (
        <div
            className={`
        relative cursor-pointer border-2 border-dashed rounded-lg transition-colors
        ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
        ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
        ${className}
      `}
            style={style}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleInputChange}
                className="hidden"
                disabled={disabled || uploading}
            />
            {children || defaultContent}
        </div>
    )
}

export default UploadFile